import { LoadedProps, RouteLoadProps, useRouteParams } from '@components/router';
import { State } from './types';
import { RuzcalMgmtPage } from '../mgmt-page';
import { PageContent, PageHeading, PageSection, defCalRoute } from '../common';
import { IcoArrowLeft, IcoExternalLink } from '@components/icons';
import { Tab, Tabs } from '@components/tabs';
import { AsyncForm } from '@components/async-form';
import { showToast } from '@components/toaster';
import { DetailsTab } from './details-tab';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { useBasicAutosaver } from '@components/autosaver';
import { QuestionsTab } from './questions-tab';
import { rpx } from 'client/lib/rpx-client';
import { Pill } from '@components/pill';
import { ResponsesTab } from './responses-tab';
import { BaseDialog } from '@components/dialog';
import { FormData, CalendarForm } from '@components/calendar-form';
import { showModalForm } from '@components/modal-form';
import { showError } from '@components/app-error';
import { LoadingIndicator } from '@components/loading-indicator';
import { useState } from 'preact/hooks';

export const route = defCalRoute({ authLevel: 'guide', load, Page });

type TabName = 'details' | 'questions' | 'responses';
async function load(opts: RouteLoadProps): Promise<State> {
  const { id } = opts.params;
  const isNew = id === 'new';

  const existing = isNew ? undefined : await rpx.ruzcalForms.getFullForm({ formId: id });

  return {
    isNew: isNew,
    id: existing?.id ?? '',
    description: existing?.description ?? '',
    name: existing?.name ?? 'Untitled form',
    questions: existing?.questions ?? [],
  };
}

type ResponseModalProps = {
  form: FormData;
  userId?: string;
  eventId?: string;
  close(): void;
};

function ResponsesModal({ form, userId, eventId, close }: ResponseModalProps) {
  return (
    <BaseDialog onClose={close} contentWidth>
      <section class="flex flex-col sm:gap-6 gap-10 sm:p-10 sm:w-2xl lg:w-4xl max-w-screen overflow-auto">
        <CalendarForm form={form} userId={userId} eventId={eventId} readonly />
      </section>
    </BaseDialog>
  );
}

async function upsertCalendarForm(state: State) {
  const form = await rpx.ruzcalForms.saveForm({
    id: state.id || undefined,
    name: state.name || 'Untitled form',
    description: state.description,
  });

  const savedQuestions = state.questions.map((question, index) => ({
    id: question.id,
    title: question.title || '',
    isRequired: question.isRequired,
    seq: index,
    isDeleted: question.isDeleted,
    type: question.type,
    answers: question.answers.map((a, index) => ({
      id: a.id,
      questionId: question.id,
      title: a.title,
      seq: index,
      deleted: a.deleted,
    })),
  }));

  if (savedQuestions.length) {
    await rpx.ruzcalForms.upsertFormQuestions({
      formId: form.id,
      questions: savedQuestions,
    });
  }

  return form;
}

function EditForm(props: LoadedProps<typeof load>) {
  const { state, setState } = props;
  const params = useRouteParams();
  const tab: TabName = (params.tab || 'details') as TabName;
  const baseURL = `/calendar/forms/${state.id}`;
  const [autosave, setAutosave] = useState(false);

  const autosaver = useBasicAutosaver(state, async (val) => {
    try {
      setAutosave(true);
      await upsertCalendarForm(val);
    } catch (err) {
      showError(err);
    } finally {
      setAutosave(false);
    }
  });

  return (
    <RuzcalMgmtPage title="Edit calendar form" currentPage="forms">
      {autosave && <LoadingIndicator />}
      <PageContent>
        <PageSection>
          <header>
            <a href="/calendar/forms" class="inline-flex items-center gap-2">
              <IcoArrowLeft />
              Back to calendar forms
            </a>
            <PageHeading
              title={
                <div class="flex items-center gap-2">
                  {state.name || 'Untitled form'}
                  {autosaver.isDirty && <Pill color="yellow">Saving...</Pill>}
                  {!autosaver.isDirty && <Pill color="green">Saved</Pill>}
                </div>
              }
              action={
                <div class="flex items-center">
                  <BtnSecondary
                    class="rounded-full border-none shadow-none p-3 leading-tight"
                    onClick={() => {
                      showModalForm(({ resolve }) => (
                        <ResponsesModal
                          form={{
                            id: state.id,
                            name: state.name,
                            description: state.description,
                            questions: state.questions,
                          }}
                          close={resolve}
                        />
                      ));
                    }}
                  >
                    <IcoExternalLink class="w-4 h-4 mr-2" /> Preview
                  </BtnSecondary>
                </div>
              }
            ></PageHeading>
          </header>
          <Tabs>
            <Tab isSelected={tab === 'details'} href={baseURL}>
              Details
            </Tab>
            <Tab
              isSelected={tab === 'questions'}
              href={`${baseURL}/questions`}
              disabled={state.id == ''}
            >
              Questions
            </Tab>
            <Tab
              isSelected={tab === 'responses'}
              href={`${baseURL}/responses`}
              disabled={state.id == ''}
            >
              Responses
            </Tab>
          </Tabs>
          <AsyncForm
            class="flex flex-col gap-14"
            onSubmit={async () => {
              await upsertCalendarForm(state);
              showToast({
                title: 'Form updated',
                type: 'ok',
                message: 'Your form has been updated successfully.',
              });
              props.router.goto(`/calendar/forms/${state.id}/questions`);
            }}
          >
            {tab == 'details' && <DetailsTab state={state} setState={setState}></DetailsTab>}
            {tab == 'questions' && state.id && (
              <QuestionsTab form={state} setForm={setState}></QuestionsTab>
            )}
            {tab == 'responses' && state.id && <ResponsesTab form={state} />}
          </AsyncForm>
        </PageSection>
      </PageContent>
    </RuzcalMgmtPage>
  );
}

function CreateForm(props: LoadedProps<typeof load>) {
  const { state, setState } = props;

  return (
    <RuzcalMgmtPage title="Edit calendar form" currentPage="forms">
      <PageContent>
        <PageSection>
          <header>
            <a href="/calendar/forms" class="inline-flex items-center gap-2">
              <IcoArrowLeft />
              Back to calendar forms
            </a>

            <PageHeading
              title={<div class="flex items-center gap-2">{state.name || 'Untitled form'}</div>}
            ></PageHeading>
          </header>

          <Tabs>
            <Tab isSelected>Details</Tab>
            <Tab disabled>Questions</Tab>
            <Tab disabled>Responses</Tab>
          </Tabs>

          <AsyncForm
            class="flex flex-col gap-14"
            onSubmit={async () => {
              const form = await upsertCalendarForm(state);
              showToast({
                title: 'Form saved',
                type: 'ok',
                message: 'Your form has been saved successfully.',
              });

              setState((prev) => ({
                ...prev,
                id: form.id,
                isNew: false,
              }));

              // Delay redirect and allow state refresh
              setTimeout(() => {
                props.router.goto(`/calendar/forms/${form.id}/questions`);
              }, 0);
            }}
          >
            <DetailsTab state={state} setState={setState}></DetailsTab>

            <footer class="flex gap-4 sticky bottom-0 py-4 z-10 px-2 -mx-2 an-slide-up bg-white">
              <BtnPrimary class="px-4">Save calendar form</BtnPrimary>
            </footer>
          </AsyncForm>
        </PageSection>
      </PageContent>
    </RuzcalMgmtPage>
  );
}

function Page(props: LoadedProps<typeof load>) {
  if (props.state.isNew) {
    return <CreateForm {...props} />;
  }
  return <EditForm {...props} />;
}
